// `src/report-artifact/printHostProtocol.ts` — the ONE definition of the
// cross-document message contract between the isolated print document and the
// application that embeds it (story 4-128d; P06 §4.3, P10 §2.3, DOD G-33).
//
// ── Why this file exists at all ────────────────────────────────────────────
// P06 §4.3: "Host the paginated print document in an isolated iframe/document
// using that artifact in the frontend. … Expose typed selection/anchor
// callbacks for Report Studio so section selection is preserved without
// importing its state into the document. **Validate any cross-document
// messages against the expected frame, origin and render ID.**"
//
// Two processes now have to agree on a wire format: the bridge that runs
// INSIDE the published artifact (`printHostBridge.ts`, bundled by
// `vite.report.config.ts`) and the React host that runs inside the
// application (`modules/reports/components/useReportPrintHost.ts`, bundled by
// `vite.config.ts`). They are two different bundles, built at two different
// times, potentially served from two different origins — so the contract
// cannot live in either of them. It lives here, is imported by both, and is
// the only place a field name, a message name or a validation rule is
// written down.
//
// This module is deliberately PURE: types, constants and pure predicates. No
// DOM, no React, no `window`. That is what lets the application import it
// without dragging Paged.js or the document tree into the app bundle, and
// what lets the artifact import it without gaining an app dependency.
//
// ── Why a message channel rather than direct property access ───────────────
// `4-128c` already publishes `window.__sitewiseReportArtifact` inside the
// artifact page, and TODAY the artifact happens to be served same-origin
// (`frontend/public/report-document/`, copied into `dist/` by the app build),
// so `iframe.contentWindow.__sitewiseReportArtifact.render(...)` would work.
// It is still the wrong boundary, for two independent reasons:
//
//   1. It validates nothing. A direct property read cannot express "this call
//      is for the frame I mounted, from the origin I expect, answering the
//      render I am currently showing" — which is literally the sentence P06
//      §4.3 requires this story to enforce.
//   2. It is same-origin-only by construction. `report-artifact/README.md` §4
//      records four real publication topologies for `4-128e`, three of which
//      put the artifact on a different origin (an R2/S3 bucket, the Pages
//      host, a registry-served path). A direct `contentWindow` reach breaks
//      on the day any of them is chosen; `postMessage` does not.
//
// ── The four validation axes, and what each one actually stops ─────────────
//   protocol   `data.protocol === PRINT_HOST_PROTOCOL`. Stops unrelated
//              `postMessage` traffic — Vite's HMR client, a browser
//              extension, React DevTools — from being parsed as a report
//              message. This is not hypothetical: a dev server broadcasts on
//              `window` constantly.
//   frame      parent side: `event.source === iframe.contentWindow`. Frame
//              side: `event.source === window.parent`. Stops a DIFFERENT
//              frame on the same page (another preview, an embedded map, an
//              ad) from driving or impersonating this one.
//   origin     parent side: `event.origin` must equal the origin the iframe
//              `src` resolves to. Frame side: `event.origin` must equal the
//              embedding origin the host declared in the frame URL. Stops a
//              cross-origin document from injecting selection events, and
//              stops the frame from answering anyone but its real host.
//   session +  a session id is minted per iframe MOUNT and carried in the
//   render id  frame URL; a render id is minted per RENDER REQUEST. Every
//              frame→host message echoes both. Stops the two staleness bugs
//              that a boundary like this always has: a reloading/replaced
//              iframe's late `ready` being taken for the current one's, and a
//              slow pagination pass answering for content the editor has
//              already typed past. P06 §4.3: "Dispose prior preview instances
//              on changes and reject late results from an older input
//              revision."
//
// All four are cheap, and all four are checked on EVERY inbound message by
// `readHostMessage` / `readFrameMessage` below — there is no "trusted" path.

import type { ReportContent } from '../lib/api/reports.gen';
import type { ReportPageGeometry } from '../report-document';

/**
 * The wire contract id. Bumped only by a breaking change to the shapes below;
 * a host and a frame that disagree on it refuse to talk rather than guess.
 * Deliberately distinct from `ARTIFACT_CONTRACT`
 * (`sitewise.report-artifact/1`, `entry.tsx`) — the RENDER protocol and the
 * MESSAGE protocol version independently: `4-128e`'s worker consumes the
 * former and never speaks the latter.
 */
export const PRINT_HOST_PROTOCOL = 'sitewise.report-print-host/1';

/**
 * Frame-URL search params. The frame cannot be told these by `postMessage`,
 * because it must already know them in order to validate the first message it
 * receives — so they travel in the URL the host chooses, which only the host
 * can choose.
 */
export const PRINT_HOST_SESSION_PARAM = 'printHostSession';
export const PRINT_HOST_ORIGIN_PARAM = 'printHostOrigin';

/**
 * Set on the frame document's `<html>` by the bridge once it is embedded
 * interactively. `printHostChrome.css` scopes every one of its rules under
 * this attribute, so the SAME artifact bytes carry the interactive chrome
 * (page shadows, gaps, badge placement, selection ring) and a worker that
 * loads the same bundle and never sets the attribute prints pure paper.
 */
export const PRINT_HOST_ATTRIBUTE = 'data-report-print-host';

/** The class the bridge toggles for the selection ring — one place, two sides. */
export const PRINT_HOST_SELECTION_CLASSES = [
  'ring-2',
  'ring-ring',
  'ring-offset-2',
  'ring-offset-background',
] as const;

// ── host → frame ────────────────────────────────────────────────────────────

/** Render (and paginate) this content. One `renderId` per request. */
export interface PrintHostRenderMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'render';
  content: ReportContent;
  variant: 'screen' | 'print';
  pendingTocPageNumbers: boolean;
  /** Applied to the fresh DOM as soon as it lands, before the host hears back. */
  selectedSectionKey: string | null;
  /** Whether the document should look clickable (Report Studio passes a handler). */
  selectable: boolean;
}

/**
 * Move the selection ring WITHOUT re-paginating. The whole reason this is a
 * separate message: a Paged.js pass is genuinely expensive (it measures real
 * box heights page by page), and selecting a section must feel instant. The
 * pre-4-128d host solved the same problem with a direct DOM class toggle for
 * exactly this reason; across the boundary it becomes one small message.
 */
export interface PrintHostSelectMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'select';
  sectionKey: string | null;
}

/**
 * Ask where a section anchor IS. P06 §4.3's "typed selection/**anchor**
 * callbacks", and the navigation half of this protocol.
 *
 * The split of labour is deliberate and follows the same sentence's rule that
 * "navigation" belongs to the HOST: only the frame can find the element (it is
 * in another document, and no `querySelector` in the embedding page will ever
 * see it), and only the host can scroll the page the frame is embedded in. So
 * the frame answers with a number — the anchor's offset inside its own
 * document — and the host does the scrolling, in its own scroll container.
 *
 * The alternative — calling `scrollIntoView` inside the frame — was built
 * first and replaced. The frame is deliberately sized to its own content (so
 * the EDITOR page scrolls the page stack, exactly as it did before the
 * boundary existed), which means the frame's own viewport has nothing left to
 * scroll: every pixel of movement has to happen in the embedding page's
 * scroll container, which only the embedding page can address. Relying on the
 * browser to propagate a scroll out of a frame would also be the one part of
 * this protocol that could not survive a cross-origin publication — the
 * topology `report-artifact/README.md` §4 records as a live option for
 * `4-128e`. A number crosses the boundary cleanly; a scroll does not.
 */
export interface PrintHostResolveAnchorMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'resolveAnchor';
  sectionKey: string;
}

export type PrintHostMessage =
  | PrintHostRenderMessage
  | PrintHostSelectMessage
  | PrintHostResolveAnchorMessage;

// ── frame → host ────────────────────────────────────────────────────────────

/** What the frame reports about the artifact it actually loaded. */
export interface PrintHostIdentity {
  contract: string;
  sourceDigest: string;
  artifactDigest: string | null;
  manifestError: string | null;
  origin: string;
}

/** The serializable half of a pagination pass. Element handles never cross. */
export interface PrintHostPagination {
  pageCount: number;
  frontMatterPageCount: number;
  bodyPageCount: number;
  /** `data-toc-key` → 1-based physical page number, produced by the SAME pass. */
  tocPageByKey: Record<string, number>;
  geometry: ReportPageGeometry;
}

/**
 * The frame is loaded and the artifact's own render protocol is available.
 * Carries the identity so the host can log/surface WHICH artifact is driving
 * the preview — the 4-128c freshness contract, visible from the app side.
 */
export interface PrintFrameReadyMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: null;
  type: 'ready';
  identity: PrintHostIdentity;
}

/** One render+paginate pass finished. Answers exactly one `renderId`. */
export interface PrintFrameRenderedMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'rendered';
  pagination: PrintHostPagination;
  /** Full document height in CSS px, so the host can size the frame to its content. */
  contentHeight: number;
}

/** The document's own height changed (fonts landed, an image decoded). */
export interface PrintFrameResizeMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'resize';
  contentHeight: number;
}

/** A real click on a section anchor inside the paper. THE selection callback. */
export interface PrintFrameSelectMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'select';
  sectionKey: string;
}

/**
 * Where a requested anchor is, in CSS px from the top of the FRAME's document.
 * `null` means the frame has no such anchor — which the host treats exactly as
 * the pre-4-128d `document.querySelector(...)?.scrollIntoView()` treated a miss:
 * it does nothing, rather than scrolling somewhere arbitrary.
 */
export interface PrintFrameAnchorMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string;
  type: 'anchor';
  sectionKey: string;
  offset: number | null;
}

/** A render or pagination pass failed. Carries a diagnostic, never content. */
export interface PrintFrameErrorMessage {
  protocol: typeof PRINT_HOST_PROTOCOL;
  session: string;
  renderId: string | null;
  type: 'error';
  message: string;
}

export type PrintFrameMessage =
  | PrintFrameReadyMessage
  | PrintFrameRenderedMessage
  | PrintFrameResizeMessage
  | PrintFrameSelectMessage
  | PrintFrameAnchorMessage
  | PrintFrameErrorMessage;

// ── validation ──────────────────────────────────────────────────────────────

const HOST_MESSAGE_TYPES = new Set(['render', 'select', 'resolveAnchor']);
const FRAME_MESSAGE_TYPES = new Set(['ready', 'rendered', 'resize', 'select', 'anchor', 'error']);

function isEnvelope(data: unknown, session: string): data is { protocol: string; session: string; type: string } {
  if (typeof data !== 'object' || data === null) return false;
  const record = data as Record<string, unknown>;
  return (
    record.protocol === PRINT_HOST_PROTOCOL &&
    typeof record.session === 'string' &&
    // Constant-shape comparison, not a secret: the session id is a same-page
    // correlation token that says "this is the frame I mounted", not an
    // authorization credential. Origin + frame identity are what authorize.
    record.session === session &&
    typeof record.type === 'string'
  );
}

/**
 * FRAME SIDE. Accept a host→frame message only if it is on-protocol, for this
 * session, from the window that embeds us, and from the origin the host
 * declared when it built our URL. Returns `null` for anything else — silently,
 * because a dev server's own broadcast traffic is not an error.
 */
export function readHostMessage(
  event: MessageEvent,
  expected: { session: string; hostOrigin: string; hostWindow: Window | null }
): PrintHostMessage | null {
  if (!expected.hostWindow || event.source !== expected.hostWindow) return null;
  if (event.origin !== expected.hostOrigin) return null;
  if (!isEnvelope(event.data, expected.session)) return null;
  if (!HOST_MESSAGE_TYPES.has(event.data.type)) return null;
  return event.data as unknown as PrintHostMessage;
}

/**
 * HOST SIDE. Accept a frame→host message only if it is on-protocol, for this
 * session, from the exact frame we mounted, and from that frame's own origin.
 *
 * The `renderId` is deliberately NOT checked here: `ready` legitimately
 * carries `null`, and the host wants to SEE a stale-render message in order to
 * drop it deliberately (and, in development, say so) rather than never
 * receiving it. That check lives at the one call site that knows which render
 * is current.
 */
export function readFrameMessage(
  event: MessageEvent,
  expected: { session: string; frameOrigin: string; frameWindow: Window | null }
): PrintFrameMessage | null {
  if (!expected.frameWindow || event.source !== expected.frameWindow) return null;
  if (event.origin !== expected.frameOrigin) return null;
  if (!isEnvelope(event.data, expected.session)) return null;
  if (!FRAME_MESSAGE_TYPES.has(event.data.type)) return null;
  return event.data as unknown as PrintFrameMessage;
}

/**
 * Build the frame URL. One function, so the two params the frame's own
 * validation depends on can never be spelled differently by the side that
 * writes them and the side that reads them.
 */
export function buildPrintFrameUrl(artifactUrl: string, session: string, hostOrigin: string): string {
  const url = new URL(artifactUrl, hostOrigin);
  url.searchParams.set(PRINT_HOST_SESSION_PARAM, session);
  url.searchParams.set(PRINT_HOST_ORIGIN_PARAM, hostOrigin);
  return url.href;
}
